import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  ChartBarIcon,
  GlobeAltIcon,
  UserGroupIcon,
  BuildingOffice2Icon,
  CircleStackIcon,
  ClipboardDocumentIcon,
  DocumentMagnifyingGlassIcon,
  TicketIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import i18n from '../i18n';

const NavItem = ({ to, icon: Icon, label }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <NavLink
      to={to}
      className={`flex items-center gap-3 px-6 py-4 transition-all text-xs font-semibold uppercase tracking-wider border-l-4 ${isActive
        ? 'bg-[#0c4352] text-white border-white/40'
        : 'text-white/60 hover:bg-white/5 hover:text-white border-transparent'
        }`}
    >
      <Icon className="w-5 h-5 opacity-80" />
      {label}
    </NavLink>
  );
};

export default function Navigation() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'ISSUING_BODY';
  const isGridOp = user?.role === 'GRID_OPERATOR' || user?.role === 'VVB_AUDITOR' || user?.role === 'ADMIN';
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  return (
    <aside className={`${isCollapsed ? 'w-20' : 'w-64'} transition-all duration-300 border-r border-gray-200 h-full sidebar-bg relative flex flex-col items-stretch`}>
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-2 top-4 w-6 h-6 bg-white border border-gray-300 rounded-full flex items-center justify-center text-gray-400 hover:text-[#155e75] shadow-sm z-50 hover:border-[#155e75] transition-all hover:scale-110 active:scale-95"
      >
        <span className="text-[10px] select-none">{isCollapsed ? '→' : '←'}</span>
      </button>

      <div className={`py-6 flex-1 overflow-y-auto overflow-x-hidden flex flex-col gap-1 ${isCollapsed ? 'items-center' : ''}`}>
        <NavItem to="/dashboard" icon={HomeIcon} label={!isCollapsed && i18n.t('home')} />
        <NavItem to="/reports" icon={ChartBarIcon} label={!isCollapsed && i18n.t('reports')} />
        <NavItem to="/devices" icon={BuildingOffice2Icon} label={!isCollapsed && i18n.t('plants')} />
        <NavItem to="/transfers" icon={GlobeAltIcon} label={!isCollapsed && i18n.t('market')} />
        <NavItem to="/certificates" icon={CircleStackIcon} label={!isCollapsed && i18n.t('certificates')} />
        {!isAdmin && <NavItem to="/my-account" icon={BuildingOffice2Icon} label={!isCollapsed && i18n.t('comp_profile')} />}
        {isAdmin && <NavItem to="/accounts" icon={UserGroupIcon} label={!isCollapsed && i18n.t('users')} />}
        {isGridOp && <NavItem to="/grid/metering" icon={TicketIcon} label={!isCollapsed && i18n.t('nav_metering')} />}
        {isAdmin && <NavItem to="/admin/approvals" icon={ClipboardDocumentIcon} label={!isCollapsed && i18n.t('approvals')} />}
        {isAdmin && <NavItem to="/admin/audit" icon={DocumentMagnifyingGlassIcon} label={!isCollapsed && i18n.t('audit')} />}
        {isAdmin && <NavItem to="/admin/participants" icon={AcademicCapIcon} label={!isCollapsed && 'Participants'} />}
        <NavItem to="/knowledge-hub" icon={AcademicCapIcon} label={!isCollapsed && (i18n.language === 'fr' ? 'Centre Savoir' : 'Knowledge Hub')} />
      </div>

      {!isCollapsed && (
        <div className="mt-auto pb-10 px-6 opacity-30">
          <p className="text-[10px] font-black text-white uppercase tracking-widest text-center italic">MAP GO</p>
          <p className="text-[8px] font-bold text-white/50 uppercase tracking-tighter text-center mt-1">GUARANTEE OF ORIGIN REGISTRY</p>
        </div>
      )}
    </aside>
  );
}
